import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table'
import { RiAddLine, RiCheckLine, RiCloseLine, RiMoreLine } from '@remixicon/react'
import {
  Alert,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  PageHeader,
  SectionSpinner,
  Select,
  Tab,
  Tabs,
  TabsList,
  TabsPanel,
  TableRoot,
  TremorTable,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TableFoot,
} from '../components/ui'
import type { AdminUser, AdminUserCreated, User } from 'shared'
import AuditLog from '../components/admin/AuditLog'
import NewUserDialog from '../components/admin/NewUserDialog'
import { useAuth } from '../contexts/AuthContext'
import SetupLinkDialog from '../components/admin/SetupLinkDialog'
import ResetLinkDialog from '../components/admin/ResetLinkDialog'
import { api, ApiError } from '../utils/api'
import { toErrorMessage } from '../lib/errors'
import { formatDate } from '../lib/dates'
import { cx } from '../lib/utils'

function IndeterminateCheckbox({
  indeterminate,
  className,
  ...rest
}: {
  indeterminate?: boolean
  className?: string
} & React.InputHTMLAttributes<HTMLInputElement>) {
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (typeof indeterminate === 'boolean' && ref.current) {
      ref.current.indeterminate = !rest.checked && indeterminate
    }
  }, [ref, indeterminate, rest.checked])

  return (
    <input
      type="checkbox"
      ref={ref}
      className={cx(
        'size-4 rounded border-gray-300 text-primary-600 shadow-sm focus:ring-primary-500 focus:ring-offset-0',
        'dark:border-gray-600 dark:bg-gray-800 dark:text-primary-500 dark:focus:ring-primary-600 dark:checked:bg-primary-600 dark:checked:border-primary-600',
        className,
      )}
      {...rest}
    />
  )
}

export default function Admin() {
  const { t, i18n } = useTranslation()
  const currentUserId = useAuth().user?.id
  const [users, setUsers] = useState<AdminUser[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [rowSelection, setRowSelection] = useState({})
  const [isUpdating, setIsUpdating] = useState(false)
  const [newUserOpen, setNewUserOpen] = useState(false)
  const [setupLink, setSetupLink] = useState<{ url: string; name: string } | null>(null)
  // L'account per cui l'admin sta chiedendo di consegnare a mano il link di reimpostazione.
  const [resetLinkFor, setResetLinkFor] = useState<{
    id: string
    name: string
    role: User['role']
  } | null>(null)
  // Cosa offrire per una password persa: l'email se c'è un canale, altrimenti un
  // link da consegnare a mano. Finché non si sa, si offre l'email, che è la regola.
  const [emailConfigured, setEmailConfigured] = useState(true)
  // Cambia a ogni azione: il registro si rilegge invece di mostrarne uno vecchio.
  const [auditVersion, setAuditVersion] = useState(0)

  useEffect(() => {
    loadUsers()
  }, [])

  const loadUsers = async () => {
    try {
      const response = await api.get<{ users: AdminUser[]; email_configured?: boolean }>(
        '/api/admin/users'
      )
      setUsers(response.users)
      setEmailConfigured(response.email_configured !== false)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.generic'))
    } finally {
      setIsLoading(false)
    }
  }

  const updateUser = async (userId: string, updates: Partial<User>) => {
    setError('')
    try {
      await api.put(`/api/admin/users/${userId}`, updates)
      await loadUsers()
      setAuditVersion((version) => version + 1)
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    }
  }

  const issueSetupLink = async (user: AdminUser) => {
    setError('')
    try {
      const response = await api.post<{ setup_url: string }>(
        `/api/admin/users/${user.id}/setup-link`,
        {}
      )
      setSetupLink({ url: response.setup_url, name: user.name })
      setAuditVersion((version) => version + 1)
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    }
  }

  const sendPasswordReset = async (user: AdminUser) => {
    setError('')
    setNotice('')
    try {
      await api.post(`/api/admin/users/${user.id}/password-reset`, {})
      setNotice(t('admin.passwordResetSent', { name: user.name, email: user.email }))
      setAuditVersion((version) => version + 1)
    } catch (err) {
      // The channel being off is a state, not a failure: say what to do about it.
      setError(
        err instanceof ApiError && err.code === 'email_disabled'
          ? t('admin.passwordResetNoEmail')
          : (toErrorMessage(err) ?? t('errors.generic'))
      )
    }
  }

  const handleCreated = (created: AdminUserCreated) => {
    setSetupLink({ url: created.setup_url, name: created.user.name })
    setAuditVersion((version) => version + 1)
    void loadUsers()
  }

  /**
   * Applica lo stesso stato a più utenti, e dice cosa non è andato.
   *
   * Le richieste partono insieme, quindi una può essere respinta mentre le
   * altre passano: la protezione dell'ultimo admin è proprio il caso. Con
   * `Promise.all` il primo rifiuto saltava il ricaricamento e lasciava a
   * schermo un elenco vecchio; `allSettled` aspetta tutte, ricarica sempre, e
   * riporta il motivo del primo rifiuto.
   */
  const applyStatus = async (targets: AdminUser[], status: User['status']) => {
    if (targets.length === 0) return

    setIsUpdating(true)
    setError('')

    const results = await Promise.allSettled(
      targets.map((user) => api.put(`/api/admin/users/${user.id}`, { status }))
    )

    await loadUsers()
    setRowSelection({})
    setAuditVersion((version) => version + 1)
    setIsUpdating(false)

    const failed = results.filter((result): result is PromiseRejectedResult => result.status === 'rejected')
    if (failed.length > 0) {
      setError(
        t('admin.bulkFailed', {
          // `failed` e non `count`: con `count` i18next cercherebbe una chiave plurale.
          failed: failed.length,
          reason: toErrorMessage(failed[0].reason) ?? t('errors.generic'),
        })
      )
    }
  }

  const handleBulkApprove = () =>
    applyStatus(
      table.getSelectedRowModel().rows.map((row) => row.original).filter((user) => user.status === 'pending'),
      'active'
    )

  const handleBulkDisable = () =>
    applyStatus(
      table.getSelectedRowModel().rows.map((row) => row.original).filter((user) => user.status === 'active'),
      'disabled'
    )

  const getStatusVariant = (status: string): 'success' | 'warning' | 'error' | 'neutral' => {
    switch (status) {
      case 'active':
        return 'success'
      case 'pending':
        return 'warning'
      case 'disabled':
        return 'error'
      default:
        return 'neutral'
    }
  }

  const selectedCount = Object.keys(rowSelection).length
  const selectedRows = useMemo(() => {
    const indices = Object.keys(rowSelection).map(Number)
    return indices.map((i) => users[i]).filter(Boolean)
  }, [rowSelection, users])

  const pendingCount = selectedRows.filter((u) => u?.status === 'pending').length
  const activeCount = selectedRows.filter((u) => u?.status === 'active').length

  const columns = useMemo<ColumnDef<AdminUser>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <IndeterminateCheckbox
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <IndeterminateCheckbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            indeterminate={row.getIsSomeSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
        enableSorting: false,
        meta: { align: 'text-left' },
      },
      {
        header: t('auth.name'),
        accessorKey: 'name',
        cell: ({ row }) => (
          <div>
            <div className="font-medium text-gray-900 dark:text-gray-50">{row.original.name}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">{row.original.email}</div>
          </div>
        ),
        enableSorting: true,
        meta: { align: 'text-left' },
      },
      {
        header: t('admin.role'),
        accessorKey: 'role',
        cell: ({ row }) => (
          <Select
            value={row.original.role}
            onChange={(e) => updateUser(row.original.id, { role: e.target.value as User['role'] })}
            options={[
              { value: 'member', label: t('admin.role_member') },
              { value: 'operator', label: t('admin.role_operator') },
              { value: 'admin', label: t('admin.role_admin') },
              { value: 'partner', label: t('admin.role_partner') },
            ]}
            className="w-32"
          />
        ),
        enableSorting: false,
        meta: { align: 'text-left' },
      },
      {
        header: t('common.status'),
        accessorKey: 'status',
        cell: ({ row }) => {
          const user = row.original

          return (
            <div className="flex flex-wrap gap-1.5">
              <Badge variant={getStatusVariant(user.status)} dot>
                {t(`admin.status${user.status.charAt(0).toUpperCase() + user.status.slice(1)}`)}
              </Badge>
              {/* Creato da un admin e non ancora attivato: è ciò che decide se
                  ha senso offrire un nuovo link. */}
              {user.setup_pending && <Badge variant="warning">{t('admin.setupPending')}</Badge>}
            </div>
          )
        },
        enableSorting: false,
        meta: { align: 'text-left' },
      },
      {
        header: t('common.createdAt'),
        accessorKey: 'created_at',
        cell: ({ row }) => formatDate(row.original.created_at, i18n.language),
        enableSorting: true,
        meta: { align: 'text-right' },
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">{t('admin.actions')}</span>,
        cell: ({ row }) => {
          const user = row.original

          // Il menu dipende da dove si trova l'account: se non ha ancora scelto la
          // password, un nuovo link di attivazione; se l'ha scelta, la
          // reimpostazione, per email o con un link da consegnare a mano. Un
          // account non attivo non riceve nessuna delle due.
          if (user.status !== 'active') return null

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  aria-label={t('admin.actionsFor', { name: user.name })}
                >
                  <RiMoreLine className="size-4" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {user.setup_pending ? (
                  <DropdownMenuItem onClick={() => issueSetupLink(user)}>
                    {t('admin.newSetupLink')}
                  </DropdownMenuItem>
                ) : (
                  <>
                    {emailConfigured ? (
                      <DropdownMenuItem onClick={() => sendPasswordReset(user)}>
                        {t('admin.sendPasswordReset')}
                      </DropdownMenuItem>
                    ) : (
                      // Senza canale email non c'è altro modo di far rientrare una
                      // persona. Non per se stessi: chi è dentro non ne ha bisogno, e
                      // sarebbe la riga su cui è più facile sbagliare.
                      user.id !== currentUserId && (
                        <DropdownMenuItem
                          onClick={() =>
                            setResetLinkFor({ id: user.id, name: user.name, role: user.role })
                          }
                        >
                          {t('admin.resetLinkMenu')}
                        </DropdownMenuItem>
                      )
                    )}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )
        },
        enableSorting: false,
        meta: { align: 'text-right' },
      },
    ],
    // I gestori toccano solo setter e `api`, che non cambiano. Ciò che il menu legge
    // per decidere cosa offrire deve invece stare nell'elenco, o la tabella resta ferma
    // al valore con cui è nata (l'email «accesa» di prima del caricamento).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- vedi sopra
    [t, i18n.language, emailConfigured, currentUserId],
  )

  const table = useReactTable({
    data: users,
    columns,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      rowSelection,
    },
  })

  return (
    <>
      <PageHeader
        title={t('admin.userManagement')}
        actions={
          <Button type="button" onClick={() => setNewUserOpen(true)}>
            <RiAddLine className="size-4" aria-hidden="true" />
            {t('admin.newUser')}
          </Button>
        }
      />

      {notice && (
        <Alert variant="success" className="mb-4">
          {notice}
        </Alert>
      )}
      {error && <Alert className="mb-4">{error}</Alert>}

      <Tabs defaultValue="users">
        <TabsList className="mb-2">
          <Tab value="users">{t('admin.usersTab')}</Tab>
          <Tab value="audit">{t('admin.auditTab')}</Tab>
        </TabsList>

        <TabsPanel value="users">
      {isLoading ? (
        <SectionSpinner />
      ) : (
        <section className="relative mb-20">
          <TableRoot className="border-t border-gray-200 dark:border-gray-800 mt-4">
              <TremorTable>
                <TableHead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHeaderCell
                          key={header.id}
                          className={cx(
                            (header.column.columnDef.meta as { align?: string })?.align,
                          )}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </TableHeaderCell>
                      ))}
                    </TableRow>
                  ))}
                </TableHead>
                <TableBody>
                  {table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      className="select-none hover:bg-gray-50 dark:hover:bg-gray-900/50"
                    >
                      {row.getVisibleCells().map((cell, index) => (
                        <TableCell
                          key={cell.id}
                          className={cx(
                            row.getIsSelected()
                              ? 'bg-gray-50 dark:bg-gray-900/50'
                              : '',
                            (cell.column.columnDef.meta as { align?: string })?.align,
                            'relative',
                          )}
                        >
                          {index === 0 && row.getIsSelected() && (
                            <div className="absolute inset-y-0 left-0 w-0.5 bg-primary-600 dark:bg-primary-500" />
                          )}
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
                <TableFoot>
                  <TableRow>
                    <TableHeaderCell colSpan={1} className="border-gray-300 dark:border-gray-800">
                      <IndeterminateCheckbox
                        checked={table.getIsAllPageRowsSelected()}
                        indeterminate={table.getIsSomePageRowsSelected()}
                        onChange={table.getToggleAllPageRowsSelectedHandler()}
                      />
                    </TableHeaderCell>
                    <TableHeaderCell colSpan={4} className="border-gray-300 font-normal normal-case tabular-nums dark:border-gray-800">
                      {selectedCount} of {table.getRowModel().rows.length} Row(s) selected
                    </TableHeaderCell>
                  </TableRow>
                </TableFoot>
              </TremorTable>
            </TableRoot>

          {/* Floating Action Bar */}
          <div
            className={cx(
              'absolute inset-x-0 -bottom-14 mx-auto flex w-fit items-center space-x-3 rounded-full border border-gray-700 bg-gray-900 px-4 py-2 text-sm font-medium shadow-lg',
              selectedCount > 0 ? '' : 'hidden',
            )}
          >
            <p className="select-none tabular-nums text-gray-400">
              {selectedCount} {t('common.selected')}
            </p>
            {pendingCount > 0 && (
              <>
                <span className="h-4 w-px bg-gray-600" aria-hidden={true} />
                <button
                  type="button"
                  onClick={handleBulkApprove}
                  disabled={isUpdating}
                  className="inline-flex items-center gap-2 text-green-400 hover:text-green-300 disabled:opacity-50"
                >
                  <RiCheckLine className="size-4" />
                  {t('admin.approve')} ({pendingCount})
                </button>
              </>
            )}
            {activeCount > 0 && (
              <>
                <span className="h-4 w-px bg-gray-600" aria-hidden={true} />
                <button
                  type="button"
                  onClick={handleBulkDisable}
                  disabled={isUpdating}
                  className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 disabled:opacity-50"
                >
                  <RiCloseLine className="size-4" />
                  {t('admin.disable')} ({activeCount})
                </button>
              </>
            )}
          </div>
        </section>
      )}
        </TabsPanel>

        <TabsPanel value="audit">
          <AuditLog version={auditVersion} />
        </TabsPanel>
      </Tabs>

      <NewUserDialog
        open={newUserOpen}
        onOpenChange={setNewUserOpen}
        onCreated={handleCreated}
      />

      <SetupLinkDialog link={setupLink} onClose={() => setSetupLink(null)} />
      <ResetLinkDialog
        user={resetLinkFor}
        onClose={() => setResetLinkFor(null)}
        onIssued={() => setAuditVersion((version) => version + 1)}
      />
    </>
  )
}
